import { useCallback, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { TaskDetail, type TaskDetailData } from '../components/task/TaskDetail';
import { DetailHeader } from '../components/DetailHeader';
import { excerptDeleteWarning } from '../lib/excerpts';

/* ── Main Component ───────────────────────────────────── */

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const confirm = useConfirm();
  const navState = location.state as { from?: string; fromLabel?: string } | null;

  const [saving, setSaving] = useState(false);
  const [task, setTask] = useState<TaskDetailData | null>(null);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const markSaved = useCallback(() => setLastSaved(new Date().toLocaleTimeString()), []);

  /* ── Delete ─────────────────────────────────────────── */

  async function handleDelete() {
    if (!taskId) return;
    const excerptNote = await excerptDeleteWarning('task', taskId);
    if (!await confirm({ title: 'Delete this task?', message: ['This cannot be undone.', excerptNote].filter(Boolean).join(' '), variant: 'danger', confirmLabel: 'Delete' })) return;
    try {
      await apiDelete(`/tasks/${taskId}`);
      toast.success('Task deleted');
      navigate('/tasks');
    } catch (err) {
      console.error('Failed to delete task:', err);
      toast.error('Failed to delete task');
    }
  }

  /* ── Render ─────────────────────────────────────────── */

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <DetailHeader
          back={{ to: navState?.from ?? '/tasks', label: navState?.fromLabel ?? 'Back to Tasks' }}
          all={navState?.from && navState.from !== '/tasks' ? { to: '/tasks', label: 'All Tasks' } : undefined}
          lastSaved={lastSaved}
          saving={saving}
          actions={[
            ...(task ? [{ kind: 'remind' as const, key: 'remind', entityType: 'task', entityId: taskId!, entityTitle: task.title }] : []),
            { key: 'delete', icon: <Trash2 size={14} />, label: 'Delete', onClick: handleDelete, danger: true },
          ]}
        />

        <TaskDetail taskId={taskId as string} onSavingChange={setSaving} onTaskLoaded={setTask} onSaved={markSaved} />
      </div>
    </div>
  );
}
